<template>
  <header
    id="main"
    :class="{ 'trial-not-expired': trialDaysLeft && customTrialHide }"
  >
    <div class="grid-container">
      <div class="col pl-0 first" v-if="!customViewAccess">
        <a id="logo" href="/" />
        <router-link v-if="canViewActivity" to="/activity" class="menu-item">
          {{ label.get("header_team_activity") }}
        </router-link>
        <router-link to="/timesheets" class="menu-item timesheet-link">
          {{ label.get("timesheets") }}
        </router-link>
        <router-link to="/payroll" class="menu-item">Payroll</router-link>
        <div class="menu-item time-clock" v-if="timeClockEnabled && !isMobile">
          <button type="button" @click="toggleTimeClock">
            <span>{{ label.get("time_clock") }}</span>
          </button>
          <div class="user-status" v-if="timeClockTimer || breakClockTimer">
            <span
              class="status-ripple"
              :class="[
                { 'on-break': breakClockTimer },
                { 'on-clock-in': !breakClockTimer },
              ]"
            ></span>
            <span class="counter">{{
              breakClockTimer ? breakClockTimer : timeClockTimer
            }}</span>
          </div>
        </div>
        <div class="menu-item time-clock" v-if="user.role_id === 1 && !isMobile">
          <button type="button" @click="redirectToKiosk">
            <span>{{ label.get("kiosk") }}</span>
          </button>
        </div>
      </div>
      <div
        v-if="trialDaysLeft && customTrialHide"
        :class="[trialDaysLeft ? 'trial-not-expired' : 'col-lg-6']"
      >
        <div class="subscribe-container">
          <p class="days-left">
            {{ trialDaysLeft }}
          </p>
          <a v-if="canViewSubscribe"
              :href="onDashboardRedirections"
              class="subscribe-btn"
              target="_blank">
            {{ label.get("subscribe_now") }}
          </a>
        </div>
      </div>
      <div class="col pl-0 last">
        <div class="user-details">
          <b-dropdown
            class="settings-dropdown"
            v-if="isConnectedToQuickbooks && user.role_id === 1 && !isMobile"
          >
            <template slot="button-content">
              <span class="name">QuickBooks Online</span>
            </template>
            <b-dropdown-item @click="importQuickbooksModal">
              {{ label.get("import") }}
            </b-dropdown-item>
            <b-dropdown-item
              href="https://help.attotime.com/article/86-how-to-integrate-to-quickbooks-online"
              target="_blank"
            >
              {{ label.get("help") }}
            </b-dropdown-item>
            <b-dropdown-item @click="disconnectQuickbookModal">
              {{ label.get("quickbook_disconnect") }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            v-if="isConnectedToXero && !isMobile && user.role_id === 1"
            right
            class="settings-dropdown"
          >
            <template slot="button-content">
              <span class="name">Xero</span>
            </template>
            <b-dropdown-item @click="exportTimesheetsModal">
              {{ "Export Timesheets" }}
            </b-dropdown-item>
            <b-dropdown-item @click="importXeroEmployeesModal">
              {{ "Import Employees" }}
            </b-dropdown-item>
            <b-dropdown-item @click="importXeroPayItemsModal">
              {{ "Update Pay Items" }}
            </b-dropdown-item>
            <b-dropdown-item @click="importXeroPayCalendarsModal">
              {{ "Update Pay Calendars" }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown right class="settings-dropdown">
            <template slot="button-content">
              <span class="name">{{ company.name }}</span>
            </template>
            <b-dropdown-item to="/settings/profile">
              <div class="icon left">
                <i class="atto atto-settings-btn settings" />
              </div>
              {{ label.get("settings") }}
              <div class="icon right">
                <i class="atto atto-r-arrow-b" />
              </div>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewManageTeam && !customViewAccess"
              to="/settings/manage-team"
            >
              <div class="icon left">
                <i class="atto atto-team manage" />
              </div>
              {{ label.get("manage_team") }}
              <div class="icon right">
                <i class="atto atto-r-arrow-b" />
              </div>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canViewSubscribe"
              :href="onDashboardRedirections"
              target="_blank"
            >
              <div class="icon left">
                <i class="atto atto-subscription-card subscription" />
              </div>
              {{ label.get("subscription") }}
              <div class="icon right">
                <i class="atto atto-r-arrow-b" />
              </div>
            </b-dropdown-item>
            <hr />
            <b-dropdown-item href="https://help.attotime.com/" target="_blank">
              <div class="icon left">
                <i class="atto atto-help-service" />
              </div>
              {{ label.get("help_center") }}
              <div class="icon right">
                <i class="atto atto-new-link" />
              </div>
            </b-dropdown-item>
            <b-dropdown-item @click="feedbackSupport">
              <div class="icon left">
                <i class="atto atto-help-support support" />
              </div>
              {{ label.get("feedback_support") }}
              <div class="icon right">
                <i class="atto atto-r-arrow-b" />
              </div>
            </b-dropdown-item>
            <hr />
            <b-dropdown-item href="/logout" @click="logout">
              <div class="icon left">
                <i class="atto atto-log-out" />
              </div>
              {{ label.get("log_out") }}
            </b-dropdown-item>
          </b-dropdown>
          <div class="user-pic">
            <p v-if="!user.avatar">
              {{ user.initials }}
            </p>
            <img v-if="user.avatar" :src="user.avatar" width="40" height="40" />
          </div>
        </div>
      </div>
    </div>
    <transition name="fade">
      <span v-if="showOverlay" class="header-overlay" />
    </transition>
  </header>
</template>
<script>
import AuthService from "../../shared/services/AuthService";
import UtilsService from "../../shared/services/UtilsService";
export default {
  name: "HeaderComponent",
  props: {
    company: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      user: AuthService.getUser(),
      accessToken: AuthService.getToken(),
      hostname: window.location.hostname,
      dropdownMenuActive: false,
      subscribed: this.$store.state.subscribed,
      trialDaysLeft: this.$store.state.company.trial_days_left,
    };
  },
  computed: {
    onDashboardRedirections() {
      return this.hostname.includes('spectalabs.dev') 
            ? `https://dashboard.atto.spectalabs.dev/subscription/management?token=${this.accessToken}` 
            : `https://dashboard.attotime.com/subscription/management?token=${this.accessToken}`
    },
    customViewAccess() {
      return false; //this.$store.state.user.id === 281511;
    },
    customTrialHide() {
      return (
        this.$router.currentRoute.path !== "/subscription/plans" &&
        this.$router.currentRoute.path !==
          "/subscription/billing-information" &&
        !(
          this.$store.state.user.id === 281511 ||
          this.$store.state.user.id === 292781 ||
          this.$store.state.user.id === 292780
        )
      );
    },
    showOverlay() {
      return this.$store.state.overlay;
    },
    canViewActivity() {
      return this.$store.state.permission.canAccessActivity;
    },
    canViewSubscribe() {
      return this.$store.state.permission.canAccessBilling;
    },
    canViewSettings() {
      return this.$store.state.permission.canAccessSettings;
    },
    canViewManageTeam() {
      return this.$store.state.permission.canAccessManageTeam;
    },
    isConnectedToQuickbooks() {
      return this.$store.state.isConnectedToQuickbooks;
    },
    isMobile() {
      return UtilsService.isMobile.any();
    },
    isConnectedToXero() {
      return this.$store.state.isConnectedToXero;
    },
    timeClockEnabled() {
      return this.$store.state.companySettings.web_timeclock;
    },
    timeClockTimer() {
      return this.$store.state.timeClockTicker;
    },
    breakClockTimer() {
      return this.$store.state.breakClockTicker;
    },
  },
  mounted() {},
  methods: {
    logout() {
      AuthService.logOut();
    },
    disconnectQuickbookModal() {
      this.$root.$emit("showDisconnectQuickbookAccount");
    },
    importQuickbooksModal() {
      if (!this.isSubscriptionExpired()) {
        this.$root.$emit("showQuickBookImport");
      } else {
        this.$root.$emit("noSubscription");
      }
    },
    isSubscriptionExpired() {
      return (
        !this.$store.state.onTrial &&
        !this.$store.state.subscribed &&
        !this.$store.state.company.trial_ends_at
      );
    },
    feedbackSupport() {
      this.$root.$emit("showFeedbackModal");
    },
    importXeroEmployeesModal() {
      if (!this.isSubscriptionExpired()) {
        this.$root.$emit("showXeroEmployeeImport");
      } else {
        this.$root.$emit("noSubscription");
      }
    },
    importXeroPayItemsModal() {
      this.$root.$emit("showXeroPayItemsImport");
    },
    exportTimesheetsModal() {
      this.$root.$emit("showXeroTimesheetsExport");
    },
    importXeroPayCalendarsModal() {
      this.$root.$emit("showXeroPayCalendars");
    },
    toggleTimeClock() {
      this.$root.$emit("onToggleTimeClock");
    },
    redirectToKiosk() {
      window.location.href = 'https://kiosk.attotime.com/';
    }
  },
};
</script>

<style lang="scss">
/* @media queries */
@mixin mobile {
  @media (max-width: 480px) {
    @content;
  }
}
/* End of: @media queries */
header {
  top: 0;
}
header.trial-not-expired {
  top: 48px;
}
header#main {
  background: #fff;
  border-bottom: 1px solid #e3e3e3;
  position: fixed;
  z-index: 11;
  left: 0;
  width: 100%;
  height: 61px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .grid-container {
    display: flex;
    padding: 0 32px 0 23px;
    align-items: center;
    width: 100%;

    .trial-not-expired {
      width: 100%;
      height: 48px;
      background: #00b9cb;
      position: absolute;
      top: -48px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      .subscribe-container {
        p {
          font-size: 0.875rem;
          line-height: 1rem;
          color: #ffffff;
          font-weight: bold;
        }
        a {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 128px;
          height: 32px;
          font-size: 0.875rem;
          line-height: 1rem;
          background: #ffffff;
          color: #262626;
          &:hover {
            background: #262626;
            color: #ffffff;
          }
        }
      }
    }

    > .col {
      display: inline-flex;
      align-items: center;

      &:last-child {
        padding-right: 0;
      }
    }

    .col {
      .time-clock {
        display: flex;
        align-items: center;
        height: 24px;
        border-left: 1px solid #e3e3e3;
        button {
          padding-left: 25px;
        }
        .user-status {
          display: flex;
          align-items: center;
          @mixin ringsStatus($duration, $delay, $bg) {
            content: "";
            position: absolute;
            height: 20px;
            width: 20px;
            background: $bg;
            border-radius: 100%;
            animation: $duration rippleStatus $delay infinite;
            opacity: 0;
            z-index: -1;
          }
          @keyframes rippleStatus {
            from {
              opacity: 0.2;
              transform: scale3d(1, 1, 1);
            }
            to {
              opacity: 0;
              transform: scale3d(1.5, 1.5, 1);
            }
          }
          .status-ripple {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 8px;
            height: 8px;
            border-radius: 100%;
            margin: 0 20px 0 15px;
            &.on-clock-in {
              background: #06b879;
              &::after {
                @include ringsStatus(4s, 4s, #06b879);
              }
              &::before {
                @include ringsStatus(4s, 0s, #06b879);
              }
            }
            &.on-break {
              background: #fba345;
              &::after {
                @include ringsStatus(4s, 4s, #fba345);
              }
              &::before {
                @include ringsStatus(4s, 0s, #fba345);
              }
            }
          }
          .counter {
            font-size: 0.9375rem;
            line-height: 1.75rem;
            color: #262626;
            font-weight: 500;
          }
        }
      }
    }

    .menu-item {
      color: $atto-font-color;
      font-family: $roboto-font;
      font-weight: 400;
      font-size: 0.9375rem;
      line-height: 1.75rem;
      margin-right: 30px;
      margin-bottom: 0;
      position: relative;
      /*width: 98px;*/

      &.router-link-active {
        color: #00b9cb;
        font-weight: 500;
      }

      &.timesheet-link {
        width: auto;
      }
    }

    .status {
      max-width: 186px;
      width: 100%;
      height: 40px;
      background-color: #f7f7f7;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        font-size: 14px;
        line-height: 24px;
        font-weight: 500;
      }
      i.activity-icon {
        margin: 0 8px;
        &.onBreak {
          background-color: $atto-orange;
        }
      }
    }

    .settings-dropdown {
      margin-right: 12px;
      margin-left: 28px;
      @include mobile {
        margin-left: 10px;
      }
      .dropdown-toggle {
        background-color: transparent;
        border: 0;
        border-radius: 0;
        display: flex;
        justify-content: center;
        align-items: center;

        // Font Styling
        color: $atto-font-color;
        font-family: $roboto-font;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.5rem;
        text-align: right;
        margin-bottom: 0;
        padding: 0;

        > span.name {
          max-width: calc(100% - 20px);
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
        }

        &::after {
          content: "\E90A";
          box-sizing: border-box;
          color: #c9c9c9;
          font-size: 10px;
          margin-left: 10px;
          font-family: "attotime" !important;
          border: 0 !important;
          speak: none;
          font-style: normal;
          font-weight: normal;
          font-variant: normal;
          text-transform: none;
          line-height: 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        &:active:focus,
        &:focus,
        &:active {
          background-color: transparent;
          border: 0;
          outline: 0;
          box-shadow: none;
          color: $atto-font-color;
        }
      }

      .dropdown-menu {
        border-radius: 0;
        border: 0;
        z-index: 10;
        background-color: #fff;
        box-shadow: 0 4px 10px 0 rgba(38, 38, 38, 0.15);
        width: 272px;
        padding: 8px 0;
        margin-top: 28px;

        &.show {
          display: flex;
          flex-direction: column;
        }

        li {
          a {
            display: inline-flex;
            padding: 12px;
            font-size: 1rem;
            line-height: 1.5rem;
            font-family: $roboto-font;
            color: $atto-font-color;
            font-weight: $roboto-regular;
            width: 100%;

            div.icon {
              width: 24px;
              height: 24px;
              display: inline-flex;
              justify-content: center;
              align-items: center;

              i {
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;

                &.atto-help-service {
                  background-image: url("../../../images/help-center-icon.svg");
                  -webkit-background-size: 19px;
                  background-size: 19px;
                  background-repeat: no-repeat;
                  background-position: center;
                }

                &.atto-new-link {
                  background-image: url("../../../images/new-tab-icon.svg");
                  -webkit-background-size: 15px;
                  background-size: 15px;
                  background-repeat: no-repeat;
                  background-position: center;
                }

                &.support {
                  font-size: 18px;
                }

                &.settings {
                  font-size: 16px;
                }

                &.manage {
                  font-size: 13px;
                }

                &.subscription {
                  font-size: 14px;
                }
              }

              &.left {
                margin-right: 12px;
              }

              &.right {
                margin-left: auto;
                margin-right: 0;

                i {
                  color: #c7c7c7;
                  font-size: 0.75rem;
                }
              }
            }

            &:hover {
              background-color: #f7f7f7;
            }
          }
        }

        hr {
          margin: 12px;
          border-color: #efefef;
        }
      }
    }

    #logo {
      width: 22px;
      height: 25px;
      display: block;
      background: url("../../../images/main-logo.svg") no-repeat center;
      background-size: contain;
      margin-right: 19px;
      margin-top: -1px;
    }

    .right {
      //margin-left: auto;
      align-items: center;
      display: flex;
      flex-direction: row;
    }

    div.subscribe-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;

      p {
        color: #00b9cb;
        font-family: $roboto-font;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.5rem;
        margin-bottom: 0;
        margin-right: 24px;
      }
    }

    div.user-details {
      margin-left: auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      position: relative;
      padding: 0;

      .company-name {
        color: $atto-font-color;
        font-family: $roboto-font;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.25rem;
        text-align: right;
        margin-bottom: 0;
        margin-right: 28px;
        cursor: pointer;

        i {
          color: #c9c9c9;
          font-size: 10px;
          margin-left: 10px;
        }

        &:hover {
          i {
            color: $atto-font-color;
          }
        }
      }

      div.user-pic {
        width: 36px;
        height: 36px;
        background-color: #efefef;
        border-radius: 100%;
        display: inline-flex;
        justify-content: center;
        align-items: center;

        p {
          margin-bottom: 0;
          font-family: $roboto-font;
          font-weight: 500;
          font-size: 0.875rem;
          line-height: 1.25rem;
        }

        img {
          border-radius: 100%;
        }
      }
    }
  }

  span.header-overlay {
    position: absolute;
    left: 0;
    top: 0;
    height: calc(100% + 1px);
    z-index: 20;
    background-color: rgba(239, 239, 239, 0.4);
    width: 100%;
  }

  .quickbooks-dropdown {
    .dropdown-toggle {
      background-color: transparent;
      border: 0;
      border-radius: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 0;
      padding: 0;

      &::after {
        content: "\E90C";
        box-sizing: border-box;
        color: #c9c9c9;
        font-size: 10px;
        margin-left: 10px;
        font-family: "attotime" !important;
        border: 0 !important;
        speak: none;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
        line-height: 1;
      }

      &:active:focus,
      &:focus,
      &:active {
        background-color: transparent;
        border: 0;
        outline: 0;
        box-shadow: none;
        color: $atto-font-color;
      }
    }

    .dropdown-image {
      display: inline-flex;
      width: 90px;
      height: 17px;
      background-image: url("~@assets/images/quickbooks-logo.svg");
    }

    .dropdown-image-xero {
      display: inline-flex;
      width: 90px;
      height: 21px;
      background-size: 80px;
      background-repeat: no-repeat;
      background-position: center;
      background-image: url("~@assets/images/xero-logo.svg");
    }

    .dropdown-menu {
      border-radius: 0;
      border: 0;
      z-index: 10;
      background-color: #fff;
      box-shadow: 0 4px 10px 0 rgba(38, 38, 38, 0.15);
      min-width: 272px;
      padding: 8px 0;
      margin-top: 27px;
    }

    li {
      a {
        display: inline-flex;
        padding: 12px;
        font-size: 1rem;
        line-height: 1.5rem;
        font-family: "Roboto", sans-serif;
        color: $atto-font-color;
        font-weight: 400;
        width: 100%;
        &:active:focus,
        &:focus,
        &:active {
          background-color: transparent;
          border: 0;
          outline: 0;
          box-shadow: none;
          color: $atto-font-color;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  header#main {
    .grid-container {
      .col.first {
        width: 22px;
        flex-grow: unset;
        padding-right: 0;

        #logo {
          margin-right: 0;
        }
      }

      .col.last {
        max-width: calc(100% - 25px);
        margin-left: auto;
      }

      .settings-dropdown {
        .dropdown-toggle {
          .name {
            max-width: 160px !important;
          }
        }
      }
    }

    .settings-dropdown {
      .dropdown-menu {
        display: none !important;
      }

      .dropdown-toggle {
        cursor: default;
        &::after {
          content: none !important;
        }
      }
    }
  }
}

@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
  .menu-item {
    display: none;
  }
}

@media only screen and (min-device-width: 768px) and (max-device-width: 992px) and (-webkit-min-device-pixel-ratio: 1) {
  .menu-item {
    display: none;
  }
}

@media only screen and (min-device-width: 1024px) and (max-device-height: 1366px) and (-webkit-min-device-pixel-ratio: 2) and (orientation: landscape) {
  /*.menu-item {*/
  /*    display: none;*/
  /*}*/
}

/* Portrait*/
@media only screen and (min-device-width: 1024px) and (max-device-height: 1366px) and (-webkit-min-device-pixel-ratio: 2) and (orientation: portrait) {
  .menu-item {
    display: none;
  }
}
</style>
